import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

const PLANS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    cta: "Scan a site",
    action: "scan",
    href: "/",
    pitch: "For founders and indie hackers who want a clear-eyed snapshot.",
    accent: false,
    features: [
      { ok: true, t: "Unlimited public scans" },
      { ok: true, t: "AI-powered risk analysis" },
      { ok: true, t: "TrustScore™ + breakdown" },
      { ok: true, t: "Embeddable RiskBadge™" },
      { ok: false, t: "Authenticated deep scan" },
      { ok: false, t: "AI-drafted policy PDFs" },
      { ok: false, t: "Projected TrustScore" },
    ],
  },
  {
    name: "Pro",
    price: "$10",
    period: "one-time · per site",
    cta: "Unlock a site",
    action: "scan",
    href: "/",
    pitch: "For SaaS teams who want hidden risks fixed — with templated policies.",
    accent: true,
    features: [
      { ok: true, t: "Everything in Free" },
      { ok: true, t: "Authenticated deep scan (login required)" },
      { ok: true, t: "AI Privacy Policy PDF" },
      { ok: true, t: "AI Terms of Service PDF" },
      { ok: true, t: "AI Cookie Policy PDF" },
      { ok: true, t: "Projected TrustScore after adoption" },
      { ok: true, t: "Owned by your Google account — yours forever" },
    ],
  },
  {
    name: "Pro Subscription",
    price: "$19",
    period: "month",
    cta: "Notify me when it launches",
    action: "coming-soon",
    badge: "Coming soon",
    pitch: "For SaaS teams that ship fast and need their compliance score to stay green.",
    accent: false,
    features: [
      { ok: true, t: "Everything in Pro" },
      { ok: true, t: "Daily or weekly monitoring per domain", note: "Ready today" },
      { ok: true, t: "Email alerts on score drops & new HIGH issues", note: "Ready today" },
      { ok: true, t: "Score-change alerts via email", note: "Ready today" },
      { ok: false, t: "RiskBadge™ analytics (views, click-through)", note: "In build" },
      { ok: false, t: "Dashboard with full scan history & trend charts", note: "In build" },
      { ok: true, t: "Cancel anytime" },
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loadingSub, setLoadingSub] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const navigate = useNavigate();

  const notifyMe = async () => {
    // Best-effort waitlist signup. Uses the /waitlist/subscription endpoint —
    // if the backend doesn't have it (yet), silently degrade to a toast so we
    // never leave the user staring at an error.
    setNotifyBusy(true);
    try {
      await apiClient.post(
        "/waitlist/subscription",
        { email: user?.email || null },
        { withCredentials: true },
      );
    } catch {
      /* endpoint may not exist — that's fine, still show the confirmation */
    } finally {
      setNotifyBusy(false);
      toast.success(
        user
          ? "You're on the waitlist — we'll email you when it launches."
          : "Thanks! Sign in with any email to get notified.",
      );
    }
  };

  const startSubscription = async () => {
    if (!user) {
      startLogin("/pricing");
      return;
    }
    setLoadingSub(true);
    try {
      const res = await apiClient.post(
        "/subscription/checkout",
        { origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start subscription");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription unavailable");
    } finally {
      setLoadingSub(false);
    }
  };

  return (
    <main className="border-b border-black" data-testid="pricing-page">
      <section className="border-b border-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / Pricing
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-3xl">
            Honest pricing. <span className="bg-[#0047FF] text-white px-2">No surprises.</span>
          </h1>
          <p className="text-[#475569] mt-5 max-w-xl text-lg">
            Start free forever. Pay $10 once when you need policy PDFs. Subscribe
            when your scores need to stay green every month.
          </p>
        </div>
      </section>

      <section className="border-b border-black bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <div
            className="grid lg:grid-cols-3 gap-px bg-[#0A0A0A] border border-[#0A0A0A]"
            data-testid="pricing-plans"
          >
            {PLANS.map((p) => (
              <div
                key={p.name}
                className={`p-8 flex flex-col ${
                  p.accent ? "bg-[#0A0A0A] text-white" : "bg-white"
                }`}
                data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`font-mono text-[11px] uppercase tracking-widest ${
                      p.accent ? "text-white/60" : "text-[#475569]"
                    }`}
                  >
                    {p.name}
                  </span>
                  {p.accent && (
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-[#0047FF] text-white px-2 py-1">
                      Most popular
                    </span>
                  )}
                  {p.badge && (
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-[#F59E0B] text-black px-2 py-1">
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-6xl leading-none tracking-tight">
                    {p.price}
                  </span>
                  <span
                    className={`text-sm pb-2 ${
                      p.accent ? "text-white/60" : "text-[#475569]"
                    }`}
                  >
                    /{p.period}
                  </span>
                </div>
                <p
                  className={`text-sm mt-4 ${
                    p.accent ? "text-white/70" : "text-[#475569]"
                  }`}
                >
                  {p.pitch}
                </p>

                {p.action === "subscribe" ? (
                  <>
                    <button
                      type="button"
                      onClick={startSubscription}
                      disabled={loadingSub}
                      className={`mt-6 px-5 py-3 text-sm font-medium text-center border ${
                        p.accent
                          ? "bg-[#0047FF] border-[#0047FF] text-white hover:bg-[#0033CC]"
                          : "bg-white border-black text-black hover:bg-[#F1F5F9]"
                      } disabled:opacity-60`}
                      data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                    >
                      {loadingSub ? (
                        <span className="inline-flex items-center gap-2 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" /> Starting…
                        </span>
                      ) : (
                        <>{p.cta} →</>
                      )}
                    </button>
                    <p
                      className={`mt-3 text-[10px] leading-relaxed ${
                        p.accent ? "text-white/70" : "text-[#475569]"
                      }`}
                      data-testid="subscription-microdisclosure"
                    >
                      Recurring monthly until cancelled.{" "}
                      <a
                        href="/legal/terms.html#subscription"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={
                          p.accent
                            ? "underline hover:text-white"
                            : "underline hover:text-[#0047FF]"
                        }
                      >
                        Cancel anytime
                      </a>
                      . By starting you waive the 14-day EU cooling-off for the first
                      period only (§5a). Scores &amp; alerts are AI-generated opinion,
                      not legal advice (§3b).
                    </p>
                  </>
                ) : p.action === "coming-soon" ? (
                  <>
                    <button
                      type="button"
                      onClick={notifyMe}
                      disabled={notifyBusy}
                      className="mt-6 px-5 py-3 text-sm font-medium text-center border border-black bg-[#F1F5F9] text-[#0A0A0A] hover:bg-[#E2E8F0] disabled:opacity-60"
                      data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                    >
                      {notifyBusy ? (
                        <span className="inline-flex items-center gap-2 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" /> Adding…
                        </span>
                      ) : (
                        <>{p.cta} →</>
                      )}
                    </button>
                    <p
                      className="mt-3 text-[10px] leading-relaxed text-[#475569]"
                      data-testid="subscription-coming-soon-note"
                    >
                      <strong>Not accepting new subscriptions yet.</strong>{" "}
                      Monitoring &amp; email alerts already work — we&apos;re holding
                      the plan back until the badge-analytics dashboard and per-domain
                      history charts ship, so every feature listed here is live from
                      day one.
                    </p>
                  </>
                ) : (
                  <Link
                    to={p.href}
                    className={`mt-6 px-5 py-3 text-sm font-medium text-center border ${
                      p.accent
                        ? "bg-[#0047FF] border-[#0047FF] text-white hover:bg-[#0033CC]"
                        : "bg-white border-black text-black hover:bg-[#F1F5F9]"
                    }`}
                    data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                  >
                    {p.cta} →
                  </Link>
                )}

                <ul className="mt-8 space-y-3 text-sm">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {f.ok ? (
                        <Check
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            p.accent ? "text-[#0047FF]" : "text-[#10B981]"
                          }`}
                        />
                      ) : (
                        <X
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            p.accent ? "text-white/30" : "text-[#94A3B8]"
                          }`}
                        />
                      )}
                      <span
                        className={
                          f.ok
                            ? ""
                            : p.accent
                              ? "text-white/40 line-through"
                              : "text-[#94A3B8] line-through"
                        }
                      >
                        {f.t}
                        {f.note && (
                          <span
                            className={`ml-2 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border align-middle ${
                              f.ok
                                ? "border-[#10B981] text-[#10B981]"
                                : "border-[#F59E0B] text-[#F59E0B]"
                            }`}
                          >
                            {f.note}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-center text-xs font-mono uppercase tracking-widest text-[#475569] mt-10">
            All plans include the AI-powered risk engine. Cancel anytime.
          </p>
        </div>
      </section>

      {null}
    </main>
  );
}
